import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiGift, FiCheckCircle, FiClock,
  FiUser, FiPhone, FiMapPin, FiEdit2, FiTrash2, FiChevronLeft,
  FiChevronRight, FiPlus, FiList, FiHome, FiXCircle, FiMinus
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { visitaAniversarioService } from '../../services/api';
import './VisitasAniversario.css';

const MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const STATUS_LABELS = {
  pendente:               { label: 'Não agendada',              cor: '#9ca3af', icone: FiClock },
  aguardando_confirmacao: { label: 'Aguardando confirmação',    cor: '#f59e0b', icone: FiClock },
  confirmada:             { label: 'Confirmada',                cor: '#10b981', icone: FiCheckCircle },
  realizada:              { label: 'Realizada',                 cor: '#3b82f6', icone: FiCheckCircle },
  cancelada:              { label: 'Cancelada',                 cor: '#ef4444', icone: FiTrash2 },
  ausente:                { label: 'Ausente (ninguém em casa)', cor: '#6366f1', icone: FiHome },
  nao_aceitou:            { label: 'Não aceitou a visita',      cor: '#dc2626', icone: FiXCircle },
  nao_respondeu:          { label: 'Não respondeu',             cor: '#78716c', icone: FiMinus },
};

// Status que removem o card da lista de pendentes
const STATUS_RESOLVIDOS = ['aguardando_confirmacao','confirmada','realizada','ausente','nao_aceitou','nao_respondeu'];

const formatarTelefoneWA = (tel) => {
  if (!tel) return null;
  const digits = String(tel).replace(/\D/g, '');
  if (!digits) return null;
  if (digits.startsWith('55') && digits.length >= 12) return digits;
  return '55' + digits;
};

const formatarTelefoneVisual = (tel) => {
  if (!tel) return '—';
  const d = String(tel).replace(/\D/g, '');
  if (d.length === 11) return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`;
  return tel;
};

const formatarDataBR = (data) => {
  const d = new Date(data);
  return `${String(d.getUTCDate()).padStart(2,'0')}/${String(d.getUTCMonth()+1).padStart(2,'0')}/${d.getUTCFullYear()}`;
};

const VisitasAniversario = () => {
  const navigate = useNavigate();
  const hoje = new Date();
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const [ano, setAno] = useState(hoje.getFullYear());
  const [carregando, setCarregando] = useState(true);
  const [aniversariantes, setAniversariantes] = useState([]);
  const [modalVisita, setModalVisita] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [salvandoRapido, setSalvandoRapido] = useState(null); // id da criança sendo processada

  useEffect(() => {
    carregar();
  }, [mes, ano]); // eslint-disable-line

  const carregar = async () => {
    try {
      setCarregando(true);
      const resp = await visitaAniversarioService.aniversariantes(mes, ano);
      setAniversariantes(resp.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar aniversariantes');
    } finally {
      setCarregando(false);
    }
  };

  const mudarMes = (delta) => {
    let novoMes = mes + delta;
    let novoAno = ano;
    if (novoMes > 12) { novoMes = 1; novoAno++; }
    if (novoMes < 1)  { novoMes = 12; novoAno--; }
    setMes(novoMes);
    setAno(novoAno);
  };

  const handleAgendarWhatsApp = async (crianca) => {
    if (!crianca.responsavel?.telefone && !crianca.telefone) {
      toast.warn('Nenhum telefone cadastrado para esta família');
      return;
    }
    // Abre janela/aba antes do await para não ser bloqueado pelo PWA
    const waWin = window.open('about:blank', '_blank');
    try {
      const resp = await visitaAniversarioService.mensagemIndividual(crianca.id);
      const { texto, telefone } = resp.data;
      const tel = formatarTelefoneWA(telefone);
      if (!tel) {
        waWin?.close();
        toast.warn('Telefone do responsável inválido');
        return;
      }
      const url = `https://wa.me/${tel}?text=${encodeURIComponent(texto)}`;
      if (waWin) {
        waWin.location.href = url;
      } else {
        window.location.href = url;
      }
    } catch (err) {
      waWin?.close();
      console.error(err);
      toast.error('Erro ao gerar mensagem');
    }
  };

  const abrirModalVisita = (crianca) => {
    const visita = crianca.visita;
    setModalVisita({
      criancaId: crianca.id,
      criancaNome: crianca.nome,
      visitaId: visita?.id || null,
      dataVisita: visita?.dataVisita
        ? new Date(visita.dataVisita).toISOString().slice(0, 10)
        : `${ano}-${String(mes).padStart(2,'0')}-${String(crianca.diaAniversario).padStart(2,'0')}`,
      horaVisita: visita?.horaVisita || '15:00',
      endereco: visita?.endereco || crianca.endereco || '',
      status: visita?.status || 'aguardando_confirmacao',
      observacoes: visita?.observacoes || '',
    });
  };

  const salvarVisita = async () => {
    if (!modalVisita.dataVisita || !modalVisita.horaVisita) {
      toast.warn('Preencha data e hora');
      return;
    }
    try {
      setSalvando(true);
      const payload = {
        criancaId: modalVisita.criancaId,
        dataVisita: modalVisita.dataVisita,
        horaVisita: modalVisita.horaVisita,
        endereco: modalVisita.endereco,
        status: modalVisita.status,
        observacoes: modalVisita.observacoes,
      };
      if (modalVisita.visitaId) {
        await visitaAniversarioService.atualizar(modalVisita.visitaId, payload);
      } else {
        await visitaAniversarioService.criar(payload);
      }
      toast.success('Visita salva!');
      setModalVisita(null);
      await carregar();
    } catch (err) {
      console.error(err);
      toast.error('Erro ao salvar visita');
    } finally {
      setSalvando(false);
    }
  };

  const excluirVisita = async () => {
    if (!modalVisita.visitaId) return;
    if (!window.confirm('Cancelar esta visita?')) return;
    try {
      await visitaAniversarioService.excluir(modalVisita.visitaId);
      toast.success('Visita removida');
      setModalVisita(null);
      await carregar();
    } catch (err) {
      console.error(err);
      toast.error('Erro ao remover');
    }
  };

  const marcarStatusRapido = async (crianca, status) => {
    const labels = {
      nao_aceitou:   'Não aceitou a visita',
      nao_respondeu: 'Não respondeu',
    };
    if (!window.confirm(`Marcar "${labels[status]}" para ${crianca.nome}?\nO card será removido da lista de pendentes.`)) return;
    try {
      setSalvandoRapido(crianca.id);
      const hoje = new Date().toISOString().slice(0, 10);
      if (crianca.visita?.id) {
        await visitaAniversarioService.atualizar(crianca.visita.id, { status });
      } else {
        await visitaAniversarioService.criar({
          criancaId: crianca.id,
          dataVisita: hoje,
          status,
        });
      }
      toast.success(`"${labels[status]}" registrado para ${crianca.nome}.`);
      await carregar();
    } catch (err) {
      console.error(err);
      toast.error('Erro ao registrar status');
    } finally {
      setSalvandoRapido(null);
    }
  };

  const handleEnviarGrupo = async () => {
    const dataVisita = modalVisita.dataVisita;
    const waWin = window.open('about:blank', '_blank');
    try {
      const resp = await visitaAniversarioService.mensagemGrupo(dataVisita);
      if (!resp.data.texto) {
        waWin?.close();
        toast.info('Nenhuma visita agendada para esta data');
        return;
      }
      const url = `https://wa.me/?text=${encodeURIComponent(resp.data.texto)}`;
      if (waWin) {
        waWin.location.href = url;
      } else {
        window.location.href = url;
      }
    } catch (err) {
      waWin?.close();
      console.error(err);
      toast.error('Erro ao gerar mensagem do grupo');
    }
  };

  // Crianças ainda pendentes = sem visita OU com status pendente/cancelada
  const naoAgendadas = useMemo(() =>
    aniversariantes.filter(c =>
      !c.visita || ['pendente', 'cancelada'].includes(c.visita.status)
    ),
  [aniversariantes]);

  const stats = useMemo(() => {
    const total    = aniversariantes.length;
    const pendentes = naoAgendadas.length;
    const agendadas = aniversariantes.filter(a =>
      a.visita && STATUS_RESOLVIDOS.includes(a.visita.status)
    ).length;
    return { total, agendadas, pendentes };
  }, [aniversariantes, naoAgendadas]);

  return (
    <div className="va-container">
      <header className="va-header">
        <div className="va-header-info">
          <h1><FiGift /> Visitas de Aniversário</h1>
          <p>Crianças (4 a 12 anos) que fazem aniversário no mês</p>
        </div>
        <button className="va-btn-secondary" onClick={() => navigate('/visitas-aniversario/agendadas')}>
          <FiList /> Visitas agendadas
        </button>
      </header>

      <div className="va-mes-seletor">
        <button onClick={() => mudarMes(-1)} aria-label="Mês anterior"><FiChevronLeft /></button>
        <div className="va-mes-label">
          <strong>{MESES[mes - 1]}</strong>
          <span>{ano}</span>
        </div>
        <button onClick={() => mudarMes(1)} aria-label="Próximo mês"><FiChevronRight /></button>
      </div>

      <div className="va-stats">
        <div className="va-stat">
          <span className="va-stat-num">{stats.total}</span>
          <span className="va-stat-label">Aniversariantes</span>
        </div>
        <div
          className="va-stat va-stat-ok va-stat-link"
          onClick={() => navigate(`/reuniao-criancas/visitas-agendadas?mes=${mes}&ano=${ano}`)}
          title="Ver visitas agendadas"
          style={{ cursor: 'pointer' }}
        >
          <span className="va-stat-num">{stats.agendadas}</span>
          <span className="va-stat-label">Agendadas ›</span>
        </div>
        <div className="va-stat va-stat-warn">
          <span className="va-stat-num">{stats.pendentes}</span>
          <span className="va-stat-label">Pendentes</span>
        </div>
      </div>

      {carregando ? (
        <div className="va-loading">Carregando...</div>
      ) : naoAgendadas.length === 0 ? (
        <div className="va-empty">
          <FiGift className="va-empty-icon" />
          <p>
            {aniversariantes.length === 0
              ? 'Nenhum aniversariante neste mês.'
              : 'Todas as visitas deste mês já estão agendadas!'}
          </p>
        </div>
      ) : (
        <div className="va-cards">
          {naoAgendadas.map(c => {
            const statusInfo = STATUS_LABELS[c.visita?.status] || STATUS_LABELS.pendente;
            const StatusIcon = statusInfo.icone;
            return (
              <article key={c.id} className="va-card">
                <div className="va-card-cabecalho">
                  <div className="va-dia-aniversario">
                    <span className="va-dia-num">{String(c.diaAniversario).padStart(2,'0')}</span>
                    <span className="va-dia-mes">{MESES[mes - 1].slice(0, 3).toUpperCase()}</span>
                  </div>
                  <div className="va-card-info">
                    <h3>{c.nome}</h3>
                    <p className="va-card-idade">
                      Completa <strong>{c.idadeCompletando}</strong> anos
                    </p>
                  </div>
                </div>

                <div className="va-card-corpo">
                  {c.responsavel && (
                    <div className="va-info-item">
                      <FiUser />
                      <div>
                        <span className="va-info-label">Responsável</span>
                        <span className="va-info-valor">{c.responsavel.nome}</span>
                      </div>
                    </div>
                  )}
                  {(c.responsavel?.telefone || c.telefone) && (
                    <div className="va-info-item">
                      <FiPhone />
                      <div>
                        <span className="va-info-label">Telefone</span>
                        <span className="va-info-valor">
                          {formatarTelefoneVisual(c.responsavel?.telefone || c.telefone)}
                        </span>
                      </div>
                    </div>
                  )}
                  {c.endereco && (
                    <div className="va-info-item">
                      <FiMapPin />
                      <div>
                        <span className="va-info-label">Endereço</span>
                        <span className="va-info-valor">{c.endereco}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="va-status-bar" style={{ borderLeftColor: statusInfo.cor }}>
                  <StatusIcon style={{ color: statusInfo.cor }} />
                  <span>{statusInfo.label}</span>
                  {c.visita?.dataVisita && (
                    <span className="va-status-data">
                      {formatarDataBR(c.visita.dataVisita)} às {c.visita.horaVisita}
                    </span>
                  )}
                </div>

                <div className="va-card-acoes">
                  <button
                    className="va-btn va-btn-wa"
                    onClick={() => handleAgendarWhatsApp(c)}
                    disabled={!c.responsavel?.telefone && !c.telefone}
                    title={(c.responsavel?.telefone || c.telefone) ? 'Enviar mensagem ao responsável' : 'Nenhum telefone cadastrado'}
                  >
                    <FaWhatsapp /> Marcar visita
                  </button>
                  <button
                    className="va-btn va-btn-primary"
                    onClick={() => abrirModalVisita(c)}
                    disabled={salvandoRapido === c.id}
                  >
                    {c.visita ? <><FiEdit2 /> Editar</> : <><FiPlus /> Registrar</>}
                  </button>
                  <button
                    className="va-btn va-btn-status-rapido va-btn-nao-respondeu"
                    onClick={() => marcarStatusRapido(c, 'nao_respondeu')}
                    disabled={salvandoRapido === c.id}
                    title="Marcar que não respondeu — remove da lista de pendentes"
                  >
                    {salvandoRapido === c.id ? '…' : <><FiMinus /> Não respondeu</>}
                  </button>
                  <button
                    className="va-btn va-btn-status-rapido va-btn-nao-aceitou"
                    onClick={() => marcarStatusRapido(c, 'nao_aceitou')}
                    disabled={salvandoRapido === c.id}
                    title="Marcar que não aceitou a visita — remove da lista de pendentes"
                  >
                    {salvandoRapido === c.id ? '…' : <><FiXCircle /> Não aceitou</>}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {modalVisita && (
        <div className="va-modal-overlay" onClick={() => setModalVisita(null)}>
          <div className="va-modal" onClick={(e) => e.stopPropagation()}>
            <header className="va-modal-header">
              <h2>{modalVisita.visitaId ? 'Editar visita' : 'Registrar visita'}</h2>
              <p>{modalVisita.criancaNome}</p>
            </header>

            <div className="va-modal-body">
              <div className="va-form-row">
                <div className="va-form-group">
                  <label>Data da visita</label>
                  <input
                    type="date"
                    value={modalVisita.dataVisita}
                    onChange={(e) => setModalVisita({ ...modalVisita, dataVisita: e.target.value })}
                  />
                </div>
                <div className="va-form-group">
                  <label>Horário</label>
                  <input
                    type="time"
                    value={modalVisita.horaVisita}
                    onChange={(e) => setModalVisita({ ...modalVisita, horaVisita: e.target.value })}
                  />
                </div>
              </div>

              <div className="va-form-group">
                <label>Endereço</label>
                <input
                  type="text"
                  value={modalVisita.endereco}
                  onChange={(e) => setModalVisita({ ...modalVisita, endereco: e.target.value })}
                  placeholder="Endereço da visita"
                />
              </div>

              <div className="va-form-group">
                <label>Status</label>
                <select
                  value={modalVisita.status}
                  onChange={(e) => setModalVisita({ ...modalVisita, status: e.target.value })}
                >
                  <option value="aguardando_confirmacao">Aguardando confirmação</option>
                  <option value="confirmada">Confirmada</option>
                  <option value="realizada">Realizada</option>
                  <option value="cancelada">Cancelada</option>
                  <option value="nao_aceitou">Não aceitou a visita</option>
                  <option value="nao_respondeu">Não respondeu</option>
                </select>
              </div>

              <div className="va-form-group">
                <label>Observações</label>
                <textarea
                  rows="2"
                  value={modalVisita.observacoes}
                  onChange={(e) => setModalVisita({ ...modalVisita, observacoes: e.target.value })}
                  placeholder="Observações (opcional)"
                />
              </div>

              {modalVisita.visitaId && modalVisita.status !== 'cancelada' && (
                <button className="va-btn va-btn-wa va-btn-full" onClick={handleEnviarGrupo}>
                  <FaWhatsapp /> Avisar grupo (todas visitas deste dia)
                </button>
              )}
            </div>

            <footer className="va-modal-footer">
              <button className="va-btn va-btn-ghost" onClick={() => setModalVisita(null)}>
                Cancelar
              </button>
              {modalVisita.visitaId && (
                <button className="va-btn va-btn-danger" onClick={excluirVisita}>
                  <FiTrash2 /> Excluir
                </button>
              )}
              <button className="va-btn va-btn-primary" onClick={salvarVisita} disabled={salvando}>
                {salvando ? 'Salvando...' : 'Salvar'}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisitasAniversario;
